import { z } from 'zod';
import { IsoTime } from './basics.js';
/**
 * The audit log as React reads it (knowledge doc sections 11, 15, 16 and 17; P8.3).
 *
 * Rule 9 writes an entry for every settings change and action, with who did it and
 * what it was before and after. Section 11 asks for an "audit log with before/after
 * comparison" in React; this is what `GET /audit-log` hands it. Admins only:
 * section 17 gives viewers servers, events and metrics, and an entry carries the
 * address someone signed in from and the settings as they stood.
 */
export const AUDIT_PAGE_DEFAULT = 50;
export const AUDIT_PAGE_MAX = 200;
/** Section 16: who wrote the entry. */
export const AuditActorType = z.enum(['user', 'agent', 'system']);
/**
 * Actions are `<subject>.<verb>` (`server.approve`, `settings.update`), so the
 * filter is a prefix: `server.` is everything done to servers. Only the characters
 * actions are written with, so what is typed cannot reach a `LIKE` as a wildcard.
 */
export const AuditActionPrefix = z
    .string()
    .trim()
    .regex(/^[a-z0-9_.-]{1,64}$/, 'Lower-case letters, digits, `.`, `_` and `-` only');
export const AuditLogQuery = z
    .object({
    actor_type: AuditActorType.optional(),
    /** A user's id, or a server's for what its agent did. */
    actor_id: z.string().trim().min(1).max(64).optional(),
    action: AuditActionPrefix.optional(),
    /** What it was done to: a server, token, layer or action id, or `global`. */
    target: z.string().trim().min(1).max(255).optional(),
    /** Inclusive; `to` is exclusive, as on `/events`. */
    from: IsoTime.optional(),
    to: IsoTime.optional(),
    limit: z.coerce.number().int().min(1).max(AUDIT_PAGE_MAX).default(AUDIT_PAGE_DEFAULT),
    cursor: z.string().max(512).optional(),
})
    .refine((query) => query.from === undefined ||
    query.to === undefined ||
    Date.parse(query.from) < Date.parse(query.to), { message: '`from` must be before `to`', path: ['from'] });
/** One entry. */
export const AuditLogEntry = z.object({
    id: z.uuid(),
    actorType: AuditActorType,
    actorId: z.string().max(255).nullable(),
    /**
     * A user's address, or a server's name, resolved here so the table reads as
     * people and machines. `null` for the system, and for an account or server that
     * no longer exists: the entry outlives what it names, and keeps its id.
     */
    actorName: z.string().max(255).nullable(),
    action: z.string().min(1).max(100),
    target: z.string().max(255).nullable(),
    /** The server's name when `target` is a server that still exists. */
    targetName: z.string().max(255).nullable(),
    /**
     * As written. Rule 5 keeps secrets out of both when they are written, which is
     * what makes them safe to show; their shape belongs to the action.
     */
    before: z.unknown(),
    after: z.unknown(),
    /** Where a person's request came from; `null` for agents and the system. */
    ip: z.string().max(64).nullable(),
    createdAt: IsoTime,
});
//# sourceMappingURL=audit.js.map